import React, { useState, useEffect } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import Takeover from '../takeover';
import classNames from 'classnames';
import { Button } from '../forms';
import ColorPicker from './color-picker';
import SaveButton from '../report/save-button';
import SpinnerDots from '../spinner/dots';
import { suggestColor } from './helpers/ui/suggest-color';
import { createMapService } from '../../services/mapService';
import './create-district.scss';
import withAuthProvider from '../../hoc/withAuthProvider';
import withFetch from '../../hoc/withFetch';

const EMPTY_STATE = {
    emptyDistrict: null,
    isSaving: false,
    isSaved: false,
    isSaveDialogOpen: false,
    isFirstFetch: true,
    color: '#ff0000',
    textColor: '#ff0000',
    addWatcher: false,
    name: ''
};  

function CreateDistrict(props) {
    const history = useHistory();
    const params = useParams();
    const mapService = createMapService(props.fetch);
    
    const [state, setState] = useState({
        ...EMPTY_STATE
    });

    // Stäng dialogen när id-parametern ändras (när man navigerar till ett nytt distrikt)
    useEffect(() => {
        if (params.id && state.isSaveDialogOpen) {
            setState({
                ...EMPTY_STATE
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [params.id]);

    function nameChange(e) {
        const name = e.currentTarget.value;
        setState(prevState => ({
            ...prevState,
            name
        }));
    }

    /**
     * Spara ett nytt distrikt (anropas från SaveDistrict när användaren klickar på spara)
     */
    async function saveSingleDistrictNew() {
        setState(prevState => ({ ...prevState, isSaving: true }));

        const { name, color, addWatcher, emptyDistrict } = state;

        try {            
            const result = await props.saveSingleDistrict({
                ...emptyDistrict,
                name, 
                color, 
                addWatcher
            });
            
            setState(prevState => ({ 
                ...prevState,
                isSaving: false,
                isSaved: true
            }));

            const salesDistrictScenarioId = result.salesDistrictScenarioId
                || emptyDistrict.salesDistrictScenarioId
                || params.scenarioId
                || props.scenarioId;
            const path = `${props.type === 'foodservice' ? `/foodservice-scenario` : `/scenario`}/${salesDistrictScenarioId}/create/${result.id}`;
            // Navigera till det nya distriktet - dialogen stängs automatiskt via useEffect när params.id ändras
            history.push(path);
        } catch (error) {
            console.error('[CreateDistrictMap] Error saving new district:', error);
            setState(prevState => ({ 
                ...prevState,
                isSaving: false
            }));
            alert('Kunde inte spara distrikt. Försök igen.');
        }
    }

    async function toggleSaveDialog() {
        if (!state.isSaveDialogOpen) {
            // Rensa state för nytt distrikt
            setState({
                ...EMPTY_STATE,
                isSaveDialogOpen: true,
                isLoading: true
            });

            try {
                // Steg 1: Hämta tomt distrikt från API
                const scenarioId = props.scenarioId || params.scenarioId;
                const emptyDistrict = await mapService.createDistrict(props.type, scenarioId);
                // Steg 2: Föreslå färg från befintlig spatialinfo-data (använd cachad data, inte ny fetch)
                let color = '#ff0000'; // Default färg
                if (props.allDistricts && props.allDistricts.length > 0) {
                    const excludeColors = props.allDistricts.map(district => {
                        return district.color || '#ff0000';
                    });
                    color = suggestColor(excludeColors);
                }

                setState(prevState => ({
                    ...prevState,
                    emptyDistrict,
                    isLoading: false,
                    color,
                    textColor: color
                }));
            } catch (error) {
                console.error('Error creating empty district:', error);
                setState(prevState => ({
                    ...prevState,
                    isLoading: false
                }));
                alert('Kunde inte skapa tomt distrikt. Försök igen.');
            }
        } else {
            setState(prevState => ({
                ...prevState,
                isSaveDialogOpen: !prevState.isSaveDialogOpen,
                isLoading: false
            }));
        }
    }

    function handleColorChange(e) {
        const color = e.currentTarget.value;
        setState(prevState => ({
            ...prevState,
            color
        }));
    }

    function handleInputColorChange(e) {
        const color = e.currentTarget.value;

        //validate color
        if (/^#[0-9A-F]{6}$/i.test(color)) {
            setState(prevState => ({
                ...prevState,
                color,
                textColor: color
            }));
        } else {
            setState(prevState => ({
                ...prevState,
                textColor: color === '' ? `#${color}` : color
            }));
        }
    }

    return (
        <>
            {/*<Link className="create-district-map__cancel" to={props.type === 'foodservice' ? '/foodservice-scenario' : '/scenario'} onClick={(e) => cancel(e)}>Avbryt</Link>*/}
            <button type="button" 
                    disabled={props.mapIsLoading}
                    className="form__button create-district-map__save"
                    onClick={() => toggleSaveDialog()}>
                <span>{props.mapIsLoading ? 'Laddar...' : '+ Skapa nytt säljdistrikt'}</span>
            </button>
            <Takeover isOpen={state.isSaveDialogOpen} toggleTakeover={() => toggleSaveDialog()} heading="Skapa nytt säljdistrikt">
                <div className={classNames({
                    'save-district': true,
                    'save-district--is-saving': props.isSaving,
                    'save-district--is-loading': state.isLoading
                })}>
                    {state.isSaveDialogOpen &&
                       <div className="save-district__content-outer">
                            <div className="save-district__content">
                                <label className="create-district-map__label">Namn</label>
                                <input className="create-district-map__input" type="text" onChange={(e) => nameChange(e)} value={state.name} />
                                <label className="create-district-map__label" htmlFor="districtcolor">Färg på området i kartan, eller välj en egen</label>
                                <ColorPicker color={state.color} 
                                            textColor={state.textColor} 
                                            onChange={(e) => handleColorChange(e)} 
                                            handleInputColorChange={(e) => handleInputColorChange(e)} 
                                            salesDistrictScenarioId={props.salesDistrictScenarioId} />
                            </div>
                            <div className="create-district-map__forms-footer">
                                <SaveButton onClick={() => saveSingleDistrictNew()} 
                                            disabled={state.isSaving || !state.name || !state.color || state.isLoading}
                                            isLoading={state.isSaving}
                                            isSaved={state.isSaved} 
                                            text="Spara & visa distriktet" 
                                            textSaving="Sparar distriktet..."
                                            textSaved="Distriktet sparades!"
                                            modifiers={{
                                                'create-district-map__create': true,
                                                'report-save-btn--centered': true
                                            }} />
                                <Button type="button" 
                                    modifiers={{
                                        'form__button--muted': true,
                                        'form__button--centered': true
                                    }}
                                    onClick={() => toggleSaveDialog()}>Avbryt</Button>
                            </div>
                        </div>
                    }
                    <SpinnerDots />
                </div>
            </Takeover>
        </>
    );
}

export default withAuthProvider(withFetch(CreateDistrict));